import { ApiError, apiFetch } from './api-fetch.ts'

// The admin panel is deliberately JSON-first, same rationale as
// `registry-api.ts` — these interfaces cover the fields the panel itself
// reads/displays; the rest of the row rides through as JSON.
export interface SessionRow {
  id: string
  rootId: string
  projectId: string
  agentDefinitionId: string
  agentRoleId: string
  harnessId: string | null
  cliProfileId: string | null
  status: string
  /** Operator label; empty means "untitled" — callers fall back to the
   * short session id via `sessionLabel`. */
  title: string
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

/** What to show for a session wherever one is named: its title if it has
 * one, else the short id. Centralized so the list, the detail heading and
 * the terminal tiles can't drift apart. */
export function sessionLabel(session: { id: string; title?: string }): string {
  const title = session.title ?? ''
  return title.trim() === '' ? session.id.slice(0, 8) : title
}

export interface SessionEventRow {
  id: string
  sessionId: string
  kind: string
  payload: unknown
  createdAt: string
  [key: string]: unknown
}

export interface SessionDetail extends SessionRow {
  events?: SessionEventRow[]
}

export interface SessionCommandRow {
  id: string
  sessionId: string
  type: string
  content: string
  status: string
  deliverWhen: string
  createdBy: string
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

/**
 * The server's ceiling on `limit`. It CLAMPS at this rather than
 * rejecting — a caller asking for 5000 gets 200 and a cursor, not a 400 —
 * so this constant is only here to keep the admin's page-size offer
 * honest. It mirrors `SESSION_LIST_MAX_LIMIT` in
 * `app/control-api/src/routes/sessions-schemas.ts`; the two cannot share a
 * module because the admin worker must not import the API's route code.
 */
export const SESSION_LIST_MAX_LIMIT = 200

/** Filters and paging for `GET /sessions`. All optional and AND-combined;
 * omitting one leaves it unfiltered. */
export interface SessionListQuery {
  rootId?: string
  harnessId?: string
  projectId?: string
  /**
   * Keep only sessions still in play. The SERVER default is `false`, which
   * is deliberate — CLI and script callers that have been reading the whole
   * table keep their behaviour — so the admin has to pass `true`
   * explicitly. See `resolveSessionsView` in `sessions-search.ts`.
   */
  liveOnly?: boolean
  /**
   * Page size. OMITTING it means every matching row — the server has no
   * default page size, deliberately (see `SESSION_LIST_MAX_LIMIT` in
   * `sessions-schemas.ts`), so an admin view that wants a page must say
   * so. Supplied, it is clamped to `SESSION_LIST_MAX_LIMIT`.
   */
  limit?: number
  /** Page cursor, EXCLUSIVE: the previous response's `nextCursor`. */
  before?: string
}

/**
 * One page of sessions.
 *
 * An envelope rather than a bare array because a caller that passes
 * `limit` gets a TRUNCATED answer, and truncation that looks like a whole
 * answer is the failure mode worth designing against — the type makes
 * `total` and `nextCursor` impossible to read past. (A caller that passes
 * no `limit` still gets every row; the server has no default page size.)
 */
export interface SessionListPage {
  /** Newest first (`created_at DESC, id DESC`), stable across calls. */
  items: SessionRow[]
  /**
   * Rows matching the filters, IGNORING `limit` and `before`. The N in
   * "showing X of N", so it does not shrink as you page — but it does
   * change when the filter set changes, `liveOnly` included.
   */
  total: number
  /**
   * Pass as `before` for the next page. `null` means last page, and is
   * guaranteed non-null only when a further row actually exists — so a
   * walk never ends on an empty page, even at an exact multiple of
   * `limit`.
   */
  nextCursor: string | null
}

export async function listSessions(
  query: SessionListQuery = {},
): Promise<SessionListPage> {
  const params = new URLSearchParams()
  if (query.rootId !== undefined) params.set('rootId', query.rootId)
  if (query.harnessId !== undefined) params.set('harnessId', query.harnessId)
  if (query.projectId !== undefined) params.set('projectId', query.projectId)
  // Sent only when the caller has an opinion, so an omitted `liveOnly`
  // genuinely means "whatever the server defaults to" rather than `false`.
  if (query.liveOnly !== undefined)
    params.set('liveOnly', String(query.liveOnly))
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  if (query.before !== undefined) params.set('before', query.before)
  const search = params.toString()
  return apiFetch<SessionListPage>(
    `/sessions${search === '' ? '' : `?${search}`}`,
  )
}

export interface CreateSessionBody {
  rootId: string
  projectId: string
  agentDefinitionId: string
  cwd?: string
  failoverConfigId?: string
}

export type FailoverSource = 'role' | 'hierarchy' | 'none'

export interface PlacementPreview {
  failoverConfigId: string | null
  source: FailoverSource
}

export function previewSessionPlacement(query: {
  rootId: string
  projectId: string
  agentDefinitionId: string
}): Promise<PlacementPreview> {
  const params = new URLSearchParams(query)
  return apiFetch<PlacementPreview>(
    `/sessions/placement-preview?${params.toString()}`,
  )
}

export function createSession(
  body: CreateSessionBody,
): Promise<{ session: SessionRow; reasoning: string[] }> {
  return apiFetch<{ session: SessionRow; reasoning: string[] }>('/sessions', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function getSession(
  id: string,
  withEvents?: number,
): Promise<SessionDetail> {
  const query = withEvents ? `?withEvents=${String(withEvents)}` : ''
  return apiFetch<SessionDetail>(`/sessions/${id}${query}`)
}

export function patchSession(
  id: string,
  body: Record<string, unknown>,
): Promise<SessionRow> {
  return apiFetch<SessionRow>(`/sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export async function listSessionCommands(
  id: string,
): Promise<SessionCommandRow[]> {
  const { items } = await apiFetch<{ items: SessionCommandRow[] }>(
    `/sessions/${id}/commands`,
  )
  return items
}

export function createSessionCommand(
  id: string,
  body: Record<string, unknown>,
): Promise<SessionCommandRow> {
  return apiFetch<SessionCommandRow>(`/sessions/${id}/commands`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export interface TermTicket {
  ticket: string
  termUrl: string
  expiresAt: string
}

export function mintTermTicket(id: string): Promise<TermTicket> {
  return apiFetch<TermTicket>(`/sessions/${id}/term-ticket`, {
    method: 'POST',
  })
}

export function deliverSessionCommandNow(
  id: string,
  cmdId: string,
): Promise<SessionCommandRow> {
  return apiFetch<SessionCommandRow>(
    `/sessions/${id}/commands/${cmdId}/deliver-now`,
    { method: 'POST' },
  )
}

export function cancelSessionCommand(
  id: string,
  cmdId: string,
): Promise<SessionCommandRow> {
  return apiFetch<SessionCommandRow>(
    `/sessions/${id}/commands/${cmdId}/cancel`,
    { method: 'POST' },
  )
}

// `ApiError` moved to `api-fetch.ts` with the shared fetch; re-exported here
// so existing importers of this module keep working.
export { ApiError }
